/**
 * Configuration Validation Utility
 * Validates that required environment variables are present on application startup
 */

interface ConfigValidationError {
  variable: string;
  message: string;
}

/**
 * Validates all required environment variables
 * Throws an error if any required variables are missing
 */
export function validateConfig(): void {
  const errors: ConfigValidationError[] = [];

  // Required environment variables
  const requiredVars = [
    {
      name: 'REACT_APP_API_BASE_URL',
      value: process.env.REACT_APP_API_BASE_URL,
      message: 'API base URL is required for backend communication'
    },
    {
      name: 'REACT_APP_AZURE_TENANT_ID',
      value: process.env.REACT_APP_AZURE_TENANT_ID,
      message: 'Azure AD Tenant ID is required for authentication'
    },
    {
      name: 'REACT_APP_AZURE_CLIENT_ID',
      value: process.env.REACT_APP_AZURE_CLIENT_ID,
      message: 'Azure AD Client ID is required for authentication'
    },
    {
      name: 'REACT_APP_REDIRECT_URI',
      value: process.env.REACT_APP_REDIRECT_URI,
      message: 'Redirect URI is required for authentication flow'
    }
  ];

  // Check each required variable
  for (const varConfig of requiredVars) {
    if (!varConfig.value || varConfig.value.trim() === '') {
      errors.push({
        variable: varConfig.name,
        message: varConfig.message
      });
    }
  }

  // If there are errors, fail fast with detailed message
  if (errors.length > 0) {
    const errorMessage = [
      '❌ Configuration Validation Failed',
      '',
      'The following required environment variables are missing or empty:',
      '',
      ...errors.map(err => `  • ${err.variable}\n    ${err.message}`),
      '',
      'Please check your .env file and ensure all required variables are set.',
      'See .env.example for reference.'
    ].join('\n');

    console.error(errorMessage);
    throw new Error('Configuration validation failed. Check console for details.');
  }

  // Log success
  console.log('✓ Configuration validation passed');
  console.log(`  API Base URL: ${process.env.REACT_APP_API_BASE_URL}`);
  console.log(`  Azure Tenant: ${process.env.REACT_APP_AZURE_TENANT_ID}`);
  console.log(`  Redirect URI: ${process.env.REACT_APP_REDIRECT_URI}`);
}

/**
 * Validates URLs to ensure they're properly formatted
 */
export function validateUrl(url: string | undefined, name: string): void {
  if (!url) return;

  try {
    new URL(url);
  } catch (error) {
    console.warn(`⚠️ ${name} may not be a valid URL: ${url}`);
  }
}

/**
 * Performs extended validation checks (non-critical)
 */
export function performExtendedValidation(): void {
  // Validate URL formats
  validateUrl(process.env.REACT_APP_API_BASE_URL, 'API Base URL');
  validateUrl(process.env.REACT_APP_REDIRECT_URI, 'Redirect URI');

  // Check for development-specific warnings
  if (process.env.NODE_ENV === 'production') {
    if (process.env.REACT_APP_DEBUG_APP === 'true') {
      console.warn('⚠️ Debug mode is enabled in production');
    }

    if (process.env.REACT_APP_DEBUG_API === 'true') {
      console.warn('⚠️ API debug mode is enabled in production');
    }

    if (process.env.REACT_APP_API_BASE_URL?.includes('localhost')) {
      console.error('❌ API Base URL points to localhost in production build');
    }
  }
}
